import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Award, Medal, TrendingUp, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { id: 'daily', label: 'Bugün' },
  { id: 'weekly', label: 'Bu Hafta' },
  { id: 'monthly', label: 'Bu Ay' },
  { id: 'all', label: 'Tüm Zamanlar' }
];

const RANK_STYLES = {
  1: { bg: 'from-amber-200 to-amber-400', icon: Trophy, text: 'text-amber-900' },
  2: { bg: 'from-stone-200 to-stone-400', icon: Award, text: 'text-stone-700' },
  3: { bg: 'from-orange-200 to-orange-400', icon: Medal, text: 'text-orange-800' },
};

const WaiterLeaderboard = ({ token }) => {
  const [period, setPeriod] = useState('daily');
  const [data, setData] = useState({ leaderboard: [] });
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/waiter-leaderboard?period=${period}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(res.data);
    } catch (e) {
      console.error('Leaderboard fetch error', e);
    } finally { setLoading(false); }
  }, [period, token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const totalRevenue = data.leaderboard.reduce((s, r) => s + r.total_revenue, 0);
  const totalOrders = data.leaderboard.reduce((s, r) => s + r.order_count, 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-playfair text-2xl font-bold text-charcoal">Garson Liderlik Tablosu</h2>
          <p className="text-charcoal/60 text-sm">Performans sıralaması ve teşvik panosu</p>
        </div>
        <div className="flex flex-wrap gap-1.5 bg-sand rounded-full p-1">
          {PERIODS.map(p => (
            <button
              key={p.id}
              onClick={() => setPeriod(p.id)}
              className={`px-3.5 py-1.5 rounded-full text-xs font-semibold transition-all ${
                period === p.id ? 'bg-charcoal text-white shadow' : 'text-charcoal/70 hover:bg-white'
              }`}
              data-testid={`leaderboard-period-${p.id}`}
            >
              {p.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div className="bg-gradient-to-br from-terracotta to-terracotta/80 text-white rounded-2xl p-5">
          <p className="text-white/80 text-xs uppercase tracking-wider">Toplam Ciro</p>
          <p className="font-playfair text-3xl font-bold mt-1">€{totalRevenue.toFixed(2)}</p>
        </div>
        <div className="bg-gradient-to-br from-basil to-basil/80 text-white rounded-2xl p-5">
          <p className="text-white/80 text-xs uppercase tracking-wider">Toplam Sipariş</p>
          <p className="font-playfair text-3xl font-bold mt-1">{totalOrders}</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-terracotta" /></div>
      ) : data.leaderboard.length === 0 ? (
        <div className="bg-white rounded-2xl p-12 text-center text-charcoal/50 ring-1 ring-charcoal/5">
          <TrendingUp className="w-12 h-12 mx-auto mb-3 text-charcoal/20" />
          <p>Bu dönem için garson siparişi bulunamadı.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.leaderboard.map((row, idx) => {
            const style = RANK_STYLES[row.rank];
            const Icon = style?.icon;
            return (
              <motion.div
                key={row.waiter_id || idx}
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.04 }}
                className={`flex items-center gap-4 p-4 rounded-2xl ring-1 ring-charcoal/5 ${row.rank <= 3 ? 'bg-gradient-to-r ' + style.bg : 'bg-white'}`}
                data-testid={`leaderboard-row-${row.rank}`}
              >
                <div className={`w-12 h-12 rounded-full flex items-center justify-center font-bold text-lg flex-shrink-0 ${row.rank <= 3 ? 'bg-white/40 ' + style.text : 'bg-stone-100 text-charcoal/60'}`}>
                  {Icon ? <Icon className="w-6 h-6" /> : `#${row.rank}`}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold truncate ${row.rank <= 3 ? style.text : 'text-charcoal'}`}>{row.waiter_name}</p>
                  <p className={`text-xs ${row.rank <= 3 ? style.text + '/80' : 'text-charcoal/50'}`}>
                    {row.order_count} sipariş · ortalama €{row.avg_order.toFixed(2)}
                  </p>
                </div>
                <div className="text-right">
                  <p className={`font-playfair text-xl font-bold ${row.rank <= 3 ? style.text : 'text-terracotta'}`}>
                    €{row.total_revenue.toFixed(2)}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WaiterLeaderboard;
